/**
 * Shared Google-auth CTA used by both the Sign-In and Sign-Up screens.
 *
 * The backend doesn't distinguish between "sign in" and "sign up" — the
 * /auth/google-native/callback path creates a new user if the Google email
 * is unknown, otherwise it logs the existing user in. We therefore expose
 * the SAME redirect flow from both screens, just with copy that matches
 * the page intent.
 *
 * Phase 16 (Google Sign-Up polish) — separate Sign-In and Sign-Up labels
 * give users confidence that they're about to perform the action they
 * actually want, even though they share a single OAuth flow under the
 * hood. We log the intent in a sessionStorage breadcrumb so the
 * post-OAuth toast can show the right copy on first paint.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const GoogleGlyph = () => (
  <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#EA4335" d="M12 11.5v3.6h5c-.21 1.1-1.4 3.2-5 3.2-3 0-5.5-2.5-5.5-5.5S8.9 7.3 12 7.3c1.7 0 2.9.7 3.5 1.3l2.4-2.3C16.4 4.9 14.4 4 12 4 7.6 4 4 7.6 4 12s3.6 8 8 8c4.6 0 7.7-3.2 7.7-7.8 0-.5-.05-.9-.13-1.3H12z" />
    <path fill="#34A853" d="M5.3 14.3l-.4.3-2.3 1.8C4 19.2 7.7 22 12 22c2.4 0 4.4-.8 5.8-2.1l-2.6-2c-.7.5-1.7.8-3.2.8-2.5 0-4.6-1.6-5.4-3.8z" />
    <path fill="#FBBC05" d="M2.6 16.4l2.7-2c.2.6.6 1.2 1 1.6V14H2.6c-.2.7-.4 1.5-.4 2.4z" opacity="0" />
  </svg>
);

export const GoogleAuthButton = ({
  mode = 'signin',         // 'signin' | 'signup'
  disabled = false,
  className = '',
  testid,
}) => {
  const [busy, setBusy] = useState(false);

  const labelIdle = mode === 'signup' ? 'Sign up with Google' : 'Sign in with Google';
  const labelBusy = mode === 'signup' ? 'Redirecting to Google…' : 'Redirecting to Google…';

  const handleClick = async () => {
    setBusy(true);
    try {
      // Breadcrumb so AuthCallback / post-OAuth toast can pick the right copy.
      try { sessionStorage.setItem('rb-google-intent', mode); } catch (_) { /* ignore */ }
      const redirect = `${window.location.origin}/auth/callback`;
      const { Auth } = await import('@/lib/api');
      const { auth_url } = await Auth.googleNativeLoginUrl(redirect);
      if (!auth_url) throw new Error('Auth URL missing');
      window.location.href = auth_url;
    } catch (err) {
      toast.error(err.message || 'Could not start Google sign-in');
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      onClick={handleClick}
      disabled={disabled || busy}
      variant="outline"
      className={`w-full gap-2 h-11 ${className}`}
      data-testid={testid || `google-${mode}-button`}
    >
      <GoogleGlyph />
      {busy ? labelBusy : labelIdle}
    </Button>
  );
};

export default GoogleAuthButton;
